import React, { useMemo, useState } from "react";
import { Pressable } from "react-native";
import { useRouter } from "expo-router";

import { EligibilityScreenLayout } from "@/blocks/EligibilityScreenLayout";
import { HouseholdIncomeCalculator } from "@/blocks/HouseholdIncomeCalculator";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnSegmentedControl } from "@/components/VnSegmentedControl";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useListEligibilityCategoriesQuery } from "@/data/queries/eligibilityCategoriesQueries";
import {
  useCurrentHouseholdMemberQuery,
  useListHouseholdMembersQuery,
} from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import { IncomePeriodType, IncomeRangeType } from "@/domain/eligibility";
import { useHouseholdFpl } from "@/hooks/useHouseholdFpl";
import { usePatientUSState } from "@/hooks/usePatientUSState";
import { formatCurrency } from "@/utils/currency";
import { getAllHouseholdMembersNames } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

export const EligibilityHouseholdIncomeScreen = () => {
  const router = useRouter();

  const { data: currentUserHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: household } = useGetHouseholdQuery();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const state = usePatientUSState();

  const { data: eligibilityCategoriesByUsState } =
    useListEligibilityCategoriesQuery({
      filters: {
        usState: state,
      },
      enabled: state !== undefined,
    });

  const householdFpl = useHouseholdFpl();

  const [incomePeriod, setIncomePeriod] = useState<IncomePeriodType>("MONTHLY");
  const [selectedIncomeRange, setSelectedIncomeRange] = useState<string | null>(
    null,
  );
  const [isBreakdownShown, setIsBreakdownShown] = useState(false);

  const uniqueFplPercentages = useMemo(() => {
    if (!eligibilityCategoriesByUsState) return [];

    const percentages = eligibilityCategoriesByUsState
      .map(category => category.fplPercentage)
      .filter((percentage): percentage is number => percentage !== null);

    return [...new Set(percentages)].sort((a, b) => a - b);
  }, [eligibilityCategoriesByUsState]);

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  const incomeRanges = useMemo(() => {
    if (!householdFpl || uniqueFplPercentages.length === 0) return [];

    const roundTo = (value: number, granularity: number) =>
      Math.round(value / granularity) * granularity;

    // Calculate the divisor based on income period
    const getDivisor = (period: IncomePeriodType) => {
      switch (period) {
        case "ANNUAL":
          return 1;
        case "MONTHLY":
          return 12;
        case "BI_WEEKLY":
          return 26; // 52 weeks / 2
        default:
          return 1;
      }
    };

    const divisor = getDivisor(incomePeriod);

    // Adaptive rounding: use $100 by default, but fall back to $10 for
    // consecutive percentages that would collide at $100 granularity.
    const baseAmounts = uniqueFplPercentages.map(
      percentage => (householdFpl * percentage) / 100 / divisor,
    );

    const roundedAmounts = baseAmounts.map((amount, index) => {
      const coarse = roundTo(amount, 100);

      if (index > 0) {
        const prevCoarse = roundTo(baseAmounts[index - 1]!, 100);
        if (coarse === prevCoarse) {
          // Collision: re-round both this and the previous with $10 granularity
          return roundTo(amount, 10);
        }
      }

      if (index < baseAmounts.length - 1) {
        const nextCoarse = roundTo(baseAmounts[index + 1]!, 100);
        if (coarse === nextCoarse) {
          return roundTo(amount, 10);
        }
      }

      return coarse;
    });

    const ranges = uniqueFplPercentages.map((_, index) => {
      const roundedAmount = roundedAmounts[index]!;

      let min: number;
      let max: number;

      if (index === 0) {
        min = 0;
        max = roundedAmount;
      } else {
        min = roundedAmounts[index - 1]! + 1;
        max = roundedAmount;
      }

      return {
        value: `${formatCurrency(min)} - ${formatCurrency(max)}`,
        min,
        max,
      };
    });

    // Add final range: last rounded amount + 1 and above (open-ended)
    const lastRounded = roundedAmounts[roundedAmounts.length - 1]!;

    ranges.push({
      value: `${formatCurrency(lastRounded + 1)}+`,
      min: lastRounded + 1,
      max: lastRounded + 1001,
    });

    return ranges;
  }, [householdFpl, uniqueFplPercentages, incomePeriod]);

  const householdMemberNames = getAllHouseholdMembersNames(householdMembers);

  const handleOptionChange = (range: IncomeRangeType) => {
    const currentMemberHouseholdId = currentUserHouseholdMember?.householdId;

    if (!currentMemberHouseholdId) return;

    setSelectedIncomeRange(range.value);

    // Calculate the annual income based on the selected range and income period for more accurate household update
    const getAnnualIncome = (
      range: IncomeRangeType,
      period: IncomePeriodType,
    ) => {
      // Midpoint to be rounded to nearest 100 for better accuracy, or min if max is null
      const midpoint =
        range.max !== null ? (range.min + range.max) / 2 : range.min;
      const roundToNearest100 = (value: number) =>
        Math.round(value / 100) * 100;

      const roundedMidpoint = roundToNearest100(midpoint);

      switch (period) {
        case "ANNUAL":
          return roundedMidpoint;
        case "MONTHLY":
          return roundedMidpoint * 12;
        case "BI_WEEKLY":
          return roundedMidpoint * 26;
        default:
          return roundedMidpoint;
      }
    };

    const annualIncome = getAnnualIncome(range, incomePeriod);

    updateHousehold(
      {
        id: currentMemberHouseholdId,
        householdData: {
          // Update with annual period midpoint of the selected range for better accuracy, or min if max is null
          householdIncome: annualIncome,
          // If the user has already gone through the eligibility flow once and is rechecking, we shouldn't update the eligibilityLastStep.
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "HOUSEHOLD_INCOME",
        },
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            router.navigate("/eligibility/special-conditions");
          }, 200);
        },
      },
    );
  };

  return (
    <EligibilityScreenLayout
      titleI18nKey="native:eligibilityHouseholdIncomeScreen.title"
      descriptionI18nKey="native:eligibilityHouseholdIncomeScreen.description1">
      <VnBox className="gap-4">
        <VnSegmentedControl<IncomePeriodType>
          value={incomePeriod}
          options={[
            {
              i18nKey:
                "native:eligibilityHouseholdIncomeScreen.incomePeriod.annual",
              value: "ANNUAL",
            },
            {
              i18nKey:
                "native:eligibilityHouseholdIncomeScreen.incomePeriod.monthly",
              value: "MONTHLY",
            },
            {
              i18nKey:
                "native:eligibilityHouseholdIncomeScreen.incomePeriod.biWeekly",
              value: "BI_WEEKLY",
            },
          ]}
          onChange={setIncomePeriod}
        />
      </VnBox>

      <VnBox>
        <VnText
          variant="lg"
          className="text-muted-foreground"
          i18nKey="native:eligibilityHouseholdIncomeScreen.description2"
        />
        <VnText variant="lgBold" className="text-muted-foreground">
          {householdMemberNames.join(", ")}
        </VnText>
      </VnBox>

      <VnBox className="gap-3">
        {incomeRanges.map(range => {
          const isSelected = selectedIncomeRange === range.value;
          return (
            <Pressable
              key={range.value}
              onPress={() => handleOptionChange(range)}
              className="w-full">
              <VnBox
                className={cn(
                  "border-border bg-secondary flex-row items-center gap-2 rounded-md border px-4 py-2",
                  {
                    "border-foreground border-2": isSelected,
                    "border-muted": !isSelected,
                  },
                )}>
                {isSelected && isUpdatingHousehold && (
                  <VnLoadingSpinner size={14} />
                )}
                <VnText
                  variant="lgMedium"
                  className={cn({
                    "font-primary-medium": isSelected,
                    "font-primary": !isSelected,
                  })}>
                  {range.value}
                </VnText>
              </VnBox>
            </Pressable>
          );
        })}
      </VnBox>

      <VnButton
        variant="link"
        className="justify-start px-0"
        onPress={() => {
          setIsBreakdownShown(!isBreakdownShown);
        }}
        i18nKey={
          isBreakdownShown
            ? "native:eligibilityHouseholdIncomeScreen.hideBreakdown"
            : "native:eligibilityHouseholdIncomeScreen.showBreakdown"
        }
      />

      {isBreakdownShown && (
        <HouseholdIncomeCalculator
          householdMemberNames={householdMemberNames}
          incomePeriod={incomePeriod}
          incomeRanges={incomeRanges}
          onUseRange={rangeValue => {
            handleOptionChange(rangeValue);
          }}
        />
      )}
    </EligibilityScreenLayout>
  );
};
